import classNames from 'classnames'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { t } from 'i18next'
import React, { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionEntity } from '@context/EntityContext'

import { Body1, Caption1, Heading6 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'

import { isTickmillSCType } from '@utils/companyName.utils'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { TestAnswerDto, TestAnswersDto } from '@model/ClientTestAnswerDto'
import { RegulatorOptionDto } from '@model/RegulatorOptionDto'
import { SupportContactsDto } from '@model/SupportContactsDto'

import { createFormField } from '@global/formField/FormField'

import '@utils/date.utils'

import { Form } from '@lib/formik'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Checkbox } from '@ui/components/Checkbox'
import { Textarea } from '@ui/components/Input/Textarea/Textarea'
import { Link } from '@ui/components/Link'

import { SupportedDateFormatType, formatDate } from '@utils/date.utils'
import { FormSubmitValues, createInputPhoneNumberFilter } from '@utils/formValidation'

import { useDispatchOpenChat } from '@hooks/useDispatchOpenChat'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'

import styles from './PersonalInfo.module.scss'

export interface PersonalInfoFormValues {
  firstName: string
  middleName: string
  lastName: string
  nativeName?: string
  countryCode: string
  phoneNumber: string
  country: string
  address: string
  city: string
  state: string
  postalCode: string
  dateOfBirth: string
  taxIdentificationNumber: string
  title: number
  email: string
  nationality: string
  isUsCitizen: boolean
  streetHouseNo?: string
}

const FormField = createFormField<PersonalInfoFormValues>()

const PersonalInfoFormPageUI: React.FC<FormikProps<PersonalInfoFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, values, supportContacts, regulators } = props

  const { t } = useTranslation()
  const entity = useSessionEntity()
  const { dispatchOpenChat, isLoading } = useDispatchOpenChat()

  const handleSubmitForm = (event: SyntheticEvent) => {
    event.preventDefault()
    setSubmitting(true)
    handleSubmit()
  }

  const renderTitle = (title: number) => {
    switch (title) {
      case 1:
        return t('Profile.Mr.')
      case 2:
        return t('Profile.Ms.')
    }
  }

  const getCountryName = (value?: string) => {
    if (!value) return undefined

    return (
      regulators?.find(
        (regulator) => regulator.country.id === value || regulator.country.name === value
      )?.country.name || value
    )
  }

  const sortByQuestionOrder = (answers: readonly TestAnswerDto[] = []) =>
    [...answers].sort((a, b) => a.question.order - b.question.order)

  const groupAnswersByQuestion = (answers: TestAnswerDto[]) => {
    const answersByQuestion = new Map<string, TestAnswerDto[]>()

    for (const answer of sortByQuestionOrder(answers)) {
      const questionId = answer.question.questionId
      const questionAnswers = answersByQuestion.get(questionId) ?? []

      questionAnswers.push(answer)
      answersByQuestion.set(questionId, questionAnswers)
    }

    return Array.from(answersByQuestion.values())
  }

  const getAnswerDisplayValue = (answer: TestAnswerDto) => {
    const value = answer.answer?.name || answer.answer?.id

    if (answer.question?.widgetType === 'country') {
      return getCountryName(value)
    }

    return value
  }

  const title = renderTitle(values.title)

  const renderAnswers = (answers: TestAnswerDto[], useTradingExperienceBadges = false) => {
    const answerGroups = groupAnswersByQuestion(answers)

    return answerGroups.map((questionAnswers) => {
      const firstAnswer = questionAnswers[0]
      const question = firstAnswer.question
      const questionKey = question.questionId

      if (question.widgetType === 'multi_select') {
        return (
          <div key={questionKey} className={styles.multiSelectField}>
            <Caption1 color='text-med-em'>{question.name}</Caption1>

            <Box flex direction='column' gap='x8' fullWidth>
              {questionAnswers.map((selectedAnswer) => (
                <Checkbox key={selectedAnswer.answer.id} checked disabled>
                  <Body1>{getAnswerDisplayValue(selectedAnswer) || t('N/A')}</Body1>
                </Checkbox>
              ))}
            </Box>
          </div>
        )
      }

      if (useTradingExperienceBadges) {
        return (
          <Box key={questionKey} flex justify='space-between' align='flex-start' gap='x8'>
            <Caption1 color='text-med-em'>{question.name}</Caption1>

            <Badge type={firstAnswer.answer.key === 'no' ? 'danger' : 'green'}>
              {getAnswerDisplayValue(firstAnswer) || t('N/A')}
            </Badge>
          </Box>
        )
      }

      return (
        <Textarea
          key={questionKey}
          label={question.name || ''}
          value={getAnswerDisplayValue(firstAnswer) || t('N/A')}
          disabled
          dynamicHeight
        />
      )
    })
  }

  return (
    <>
      <div className={styles.topInfo}>
        <p>
          {t('Profile.For assistance or queries, please contact')}
          <Link size='lg' onClick={dispatchOpenChat} disabled={isLoading}>
            {t('Profile.Client Support')}
          </Link>
        </p>
        <p>
          {t(
            'Profile.Subject to our policy and to safeguard your account, please inform Tickmill in case of any changes related to your personal details'
          )}
        </p>
        <p>
          {t('Profile.Telephone')}: {supportContacts?.supportPhoneNumber} | {t('Email')}:{' '}
          {supportContacts?.supportEmail}
        </p>
      </div>
      <div>
        <Form autoComplete='off' className={styles.form} onSubmit={handleSubmitForm}>
          <div className='columns is-gapless'>
            <div className='column'>
              <section className={classNames(styles.section)}>
                <Heading6 medium>{t('Personal Details')}</Heading6>
                <SensitiveData>
                  <FieldWrapper>
                    <div className='column is-8 is-half-mobile'>
                      <FormField value={title} label={t('Profile.Title')} required disabled />
                    </div>

                    <div className={classNames(styles.formContainer, styles.isColumnMobile)}>
                      <div className={styles.formFieldHalf}>
                        <FormField
                          name='firstName'
                          label={t('Profile.First Name')}
                          type='text'
                          required
                          disabled
                        />
                      </div>
                      <div className={styles.formFieldHalf}>
                        <FormField
                          name='middleName'
                          type='text'
                          label={t('Profile.Middle Name')}
                          required
                          disabled
                        />
                      </div>
                    </div>
                    <FormField
                      name='lastName'
                      label={t('Profile.Last Name')}
                      type='text'
                      required
                      disabled
                    />
                    {values.nativeName && (
                      <FormField
                        name='nativeName'
                        label={t('Profile.Native Name')}
                        type='text'
                        required
                        disabled
                      />
                    )}
                    <FormField
                      name={'dateOfBirth'}
                      label={t('Profile.Date Of Birth')}
                      type='text'
                      required
                      disabled
                    />
                    {!isTickmillSCType(entity) && (
                      <FormField
                        name={'taxIdentificationNumber'}
                        label={t('Profile.Tax Identification Number / Goods & Services Tax Number')}
                        required
                        disabled
                      />
                    )}
                    <FormField
                      name={'email'}
                      label={t('Profile.Email Address')}
                      type='text'
                      required
                      disabled
                    />
                    <div className={classNames(styles.formContainer, styles.isRowMobile)}>
                      <div className={styles.formFieldWide}>
                        <FormField
                          name={'countryCode'}
                          label={t('Profile.Country Code')}
                          type='text'
                          required
                          disabled
                        />
                      </div>
                      <div className={styles.formFieldFull}>
                        <FormField
                          name={'phoneNumber'}
                          label={t('PhoneNumbers.Phone Number')}
                          type='tel'
                          min='0'
                          autoComplete='false'
                          onInput={createInputPhoneNumberFilter()}
                          inputMode='numeric'
                          required
                          disabled
                        />
                      </div>
                    </div>
                  </FieldWrapper>
                </SensitiveData>
              </section>
              <section className={classNames(styles.section)}>
                <Heading6 medium>{t('Profile.Nationality & Address')}</Heading6>
                <div>
                  <SensitiveData>
                    <FieldWrapper>
                      <FormField
                        name={'country'}
                        disabled
                        label={t('Profile.Country of Residence')}
                        type='text'
                        required
                      />
                      <FormField
                        name='nationality'
                        label={t('Profile.Nationality')}
                        disabled
                        type='select'
                        required
                      />
                      <FormField
                        name={'address'}
                        label={t('Profile.Address Line')}
                        type='text'
                        required
                        disabled
                      />
                      <FormField
                        name={'city'}
                        label={t('Profile.City')}
                        type='text'
                        required
                        disabled
                      />
                      <div className={classNames(styles.formContainer, styles.isRowMobile)}>
                        <div className={styles.formFieldHalf}>
                          <FormField
                            name={'state'}
                            label={t('Profile.State/Region')}
                            type='text'
                            required
                            disabled
                          />
                        </div>
                        <div className={styles.formFieldHalf}>
                          <FormField
                            name={'postalCode'}
                            label={t('Profile.Postal Code')}
                            type='text'
                            required
                            disabled
                          />
                        </div>
                      </div>

                      <FormField
                        name={'streetHouseNo'}
                        label={t('Sign up.Street/House. Number')}
                        type='text'
                        disabled
                      />
                    </FieldWrapper>
                  </SensitiveData>
                </div>
                <Heading6 className='pt-4' medium>
                  {t('Profile.US Citizen')}
                </Heading6>
                <SensitiveData>
                  <Checkbox checked={props.values.isUsCitizen} disabled>
                    {t('Sign up.I am a US Citizen for tax purposes')}
                  </Checkbox>
                </SensitiveData>
              </section>
            </div>
            <div className='column'>
              {props.appTestAnswers?.sections?.map((section) => {
                const tradingExperienceBadge = section.code === 'trading_experience'

                return (
                  <section key={section.id} className={classNames(styles.section)}>
                    <Heading6 medium>{section.name}</Heading6>
                    <SensitiveData>
                      {tradingExperienceBadge ? (
                        <FieldWrapper>{renderAnswers(section.answers, true)}</FieldWrapper>
                      ) : (
                        <FieldWrapper>{renderAnswers(section.answers)}</FieldWrapper>
                      )}
                    </SensitiveData>
                  </section>
                )
              })}

              {props.economicProfileAnswers?.sections?.map((section) => (
                <section key={section.id} className={styles.section}>
                  <Heading6 medium>{section.name}</Heading6>
                  <SensitiveData>
                    <FieldWrapper>{renderAnswers(section.answers)}</FieldWrapper>
                  </SensitiveData>
                </section>
              ))}
            </div>
          </div>
        </Form>
      </div>
    </>
  )
}

interface OuterProps {
  appTestAnswers?: TestAnswersDto
  economicProfileAnswers?: TestAnswersDto
  supportContacts?: SupportContactsDto
  account?: AccountDetailedDto
  regulators?: RegulatorOptionDto[]

  onSubmit(values: FormSubmitValues<PersonalInfoFormValues>): Promise<void>
}

export const PersonalInfoFormPage = withFormik<OuterProps, PersonalInfoFormValues>({
  mapPropsToValues: ({ account }) => {
    return {
      firstName: account?.firstname || '',
      countryCode:
        account?.phoneNumbers.find((x) => x.isPrimary)?.countryCode?.replace('+', '') || '',
      lastName: account?.lastname || '',
      middleName: account?.middlename || '',
      nativeName: account?.nativeName || '',
      phoneNumber: account?.phoneNumbers.find((x) => x.isPrimary)?.number || '',
      address: account?.addresses.find((x) => x.isPrimary)?.street || '',
      city: account?.addresses.find((x) => x.isPrimary)?.city || '',
      state: account?.addresses.find((x) => x.isPrimary)?.state || '',
      postalCode: account?.addresses.find((x) => x.isPrimary)?.postalCode || '',
      country: account?.addresses.find((x) => x.isPrimary)?.country.name || '',
      dateOfBirth: account?.birthday
        ? formatDate(account?.birthday, {
            formatType: (account.dateFormatType?.name || 'dd.MM.yyyy') as SupportedDateFormatType,
            noTimezone: true,
          })
        : '',
      taxIdentificationNumber: account?.taxId || '',
      title: account?.gender.id || 1,
      email: account?.emails.find((x) => x.isPrimary)?.address || '',
      nationality: account?.nationalities.map((n) => n.name).toString() || '',
      isUsCitizen: account?.isUsCitizen || false,
      streetHouseNo: account?.addresses.find((x) => x.isPrimary)?.streetHouseNo || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<PersonalInfoFormValues> = {}
    if (!values.title) {
      errors.title = t('Validation.Required')
    }
    if (!values.firstName) {
      errors.firstName = t('Validation.Required')
    }
    if (!values.lastName) {
      errors.lastName = t('Validation.Required')
    }
    if (!values.countryCode) {
      errors.countryCode = t('Validation.Required')
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = t('Validation.Required')
    }
    if (!values.city) {
      errors.city = t('Validation.Required')
    }
    if (!values.phoneNumber) {
      errors.phoneNumber = t('Validation.Required')
    }
    if (!values.dateOfBirth) {
      errors.dateOfBirth = t('Validation.Required')
    }
    if (!values.taxIdentificationNumber) {
      errors.taxIdentificationNumber = t('Validation.Required')
    }
    if (!values.email) {
      errors.email = t('Validation.Required')
    }
    if (!values.address) {
      errors.address = t('Validation.Required')
    }
    if (!values.nationality) {
      errors.nationality = t('Validation.Required')
    }
    if (!values.state) {
      errors.state = t('Validation.Required')
    }
    if (!values.postalCode) {
      errors.postalCode = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
})(PersonalInfoFormPageUI)
